import { useState } from "react";
import { LocationPicker, type LocationData, AddressAutocomplete, MapPicker } from "@/components/location";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Utensils, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { type GeocodingResult } from "@/services/geocoding";

export function LocationDemoPage() {
  // Combined LocationPicker state
  const [location, setLocation] = useState<LocationData | null>(null);
  
  // Standalone components state
  const [autocompleteResult, setAutocompleteResult] = useState<GeocodingResult | null>(null);
  const [mapLocation, setMapLocation] = useState<{ lat: number; lon: number } | null>(null);
  const [mapAddress, setMapAddress] = useState<GeocodingResult | null>(null);

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl flex items-center justify-center">
                <Utensils className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-display font-bold text-gray-900">Rango</span>
            </Link>
            <Link to="/">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Voltar
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h1 className="text-3xl font-display font-bold text-gray-900 mb-4">
            Seletor de Localização
          </h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Demonstração dos componentes de geolocalização usando MapLibre GL JS e Nominatim (OpenStreetMap).
            Busca de endereços otimizada para o Brasil.
          </p>
        </div>

        <div className="space-y-8">
          {/* Combined LocationPicker */}
          <Card>
            <CardHeader>
              <CardTitle>LocationPicker (Componente Completo)</CardTitle>
              <CardDescription>
                Combina busca de endereço, botão de localização e mapa interativo.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <LocationPicker
                value={location}
                onChange={setLocation}
                placeholder="Digite seu endereço..."
                showMap={true}
                mapHeight="350px"
              />
              
              {location && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-medium text-sm text-gray-700 mb-2">Dados selecionados:</h4>
                  <pre className="text-xs text-gray-600 overflow-auto">
                    {JSON.stringify(location, null, 2)}
                  </pre>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Standalone AddressAutocomplete */}
          <Card>
            <CardHeader>
              <CardTitle>AddressAutocomplete (Standalone)</CardTitle>
              <CardDescription>
                Campo de busca de endereço com autocomplete usando a API do Nominatim.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <AddressAutocomplete
                value={autocompleteResult}
                onChange={setAutocompleteResult}
                placeholder="Buscar endereço..."
                countryCode="br"
              />
              
              {autocompleteResult && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-medium text-sm text-gray-700 mb-2">Resultado:</h4>
                  <p className="text-sm text-gray-600">{autocompleteResult.displayName}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Lat: {autocompleteResult.lat}, Lon: {autocompleteResult.lon}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Standalone MapPicker */}
          <Card>
            <CardHeader>
              <CardTitle>MapPicker (Standalone)</CardTitle>
              <CardDescription>
                Mapa interativo usando MapLibre GL JS com tiles gratuitos do OpenFreeMap.
                Clique no mapa para selecionar um local.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <MapPicker
                selectedLocation={mapLocation}
                onLocationSelect={(coords, address) => {
                  setMapLocation(coords);
                  setMapAddress(address || null);
                }}
                className="h-[400px]"
                showLocateButton={true}
                reverseGeocodeOnSelect={true}
              />
              
              {mapLocation && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-medium text-sm text-gray-700 mb-2">Local selecionado:</h4>
                  <p className="text-sm text-gray-600">
                    Lat: {mapLocation.lat.toFixed(6)}, Lon: {mapLocation.lon.toFixed(6)}
                  </p>
                  {mapAddress && (
                    <p className="text-xs text-gray-500 mt-1">{mapAddress.displayName}</p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Technical details */}
          <Card>
            <CardHeader>
              <CardTitle>Detalhes Técnicos</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none">
              <ul className="text-sm text-gray-600 space-y-2">
                <li>
                  <strong>Mapa:</strong> MapLibre GL JS v5 - fork open-source do Mapbox GL JS
                </li>
                <li>
                  <strong>Tiles:</strong> OpenFreeMap - tiles vetoriais gratuitos baseados no OpenStreetMap
                </li>
                <li>
                  <strong>Geocoding:</strong> Nominatim API (OpenStreetMap) - limitado a 1 req/segundo
                </li>
                <li>
                  <strong>Rate Limiting:</strong> Implementado no cliente para respeitar os limites do Nominatim
                </li>
                <li>
                  <strong>Debounce:</strong> 400ms no autocomplete para evitar requisições excessivas
                </li>
                <li>
                  <strong>Foco:</strong> Brasil (countryCode: br) para melhores resultados
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}

